import { z } from 'zod';
/**
 * A company's own brand, shown where its residents and visitors look: the
 * resident portal (sign-in band, home header, the move-in welcome) and a logo
 * chip in the office menu. TendedLoop stays the app's name; the brand is the
 * building's identity inside it.
 *
 * Stored as JSON on the tenant (`brand_profile`). Unknown or invalid profiles
 * read as "no brand", so a bad value can never break a screen.
 */
// eslint-disable-next-line no-control-regex
const NO_CONTROL = /^[^\u0000-\u001F\u007F\uFFFE\uFFFF]*$/;
const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Expected #RRGGBB');
/**
 * Where a brand file is: a storage key in the tenant's own brand folder
 * ("brand/{tenantId}/{file}", how new saves store it), an absolute https URL
 * (older saves; http only for a local portal), or a file the resident portal
 * ships ("/brands/..."). Which of these a tenant may use is checked on the
 * server (canonicalAssetRef); this only keeps out markup and other schemes.
 */
const asset = z
    .string()
    .max(500)
    .refine((v) => /^brand\/[A-Za-z0-9_-]+\/[A-Za-z0-9._-]+$/.test(v) ||
    /^\/(?!\/)[A-Za-z0-9/_.-]+$/.test(v) ||
    /^https:\/\/[^\s"'<>]+$/.test(v) ||
    // A local portal in development serves its shipped files over http.
    /^http:\/\/(?:localhost|127\.0\.0\.1)(?::\d{2,5})?\/[^\s"'<>]*$/.test(v), 'Expected a brand file key, /path or https URL');
const brandName = z.string().trim().min(1).max(80).regex(NO_CONTROL, 'No control characters');
export const tenantBrandSchema = z.object({
    /** How the building is named to residents, e.g. "Young Eliyahu". */
    displayName: brandName.optional(),
    /** The name per language, e.g. { he: "יאנג אליהו", en: "Young Eliyahu" }; falls back to displayName. */
    names: z
        .record(z.string().regex(/^[a-z]{2}(?:-[A-Z]{2})?$/, 'Expected a language code'), brandName)
        .refine((m) => Object.keys(m).length <= 12, 'At most 12 languages')
        .optional(),
    /** Logo artwork made for a black background (light and gold parts disappear on white). */
    logoOnDarkUrl: asset.optional(),
    /**
     * The same logo drawn for a light background (e.g. white lettering recoloured
     * dark). The office shows it on its own light surfaces, with no band; without
     * it the office sets the band logo on its band. Part of the verified identity.
     */
    logoOnLightUrl: asset.optional(),
    /**
     * The background the logos are drawn for. "dark" (the default) sets them on a
     * black band; "light" on a white one, for logos with dark artwork.
     */
    logoBackground: z.enum(['dark', 'light']).optional(),
    /** The same logo as PNG, for emails (many mail apps do not show WebP). */
    emailLogoUrl: asset.optional(),
    /** A second mark shown beside the first, e.g. the developer. Also artwork for black. */
    partnerLogoOnDarkUrl: asset.optional(),
    /** A decorative strip set on the edge of a black band (never behind text). */
    splashUrl: asset.optional(),
    /** A photo of the homes, for the resident's home screen. */
    heroImageUrl: asset.optional(),
    /** A square icon for the home-screen app, rendered from the logo. */
    appIconUrl: asset.optional(),
    /**
     * The 1200x630 image a shared link to the public pages shows (og:image),
     * rendered by the server from the public brand. Never taken from the input.
     */
    socialImageUrl: asset.optional(),
    /** The hero is a render, not a photo: shown with "הדמיה". */
    heroIsRender: z.boolean().optional(),
    /** Actions and links. Darkened automatically if it is too light for white text. */
    primary: hex.optional(),
    /** "Done" marks. Darkened automatically on white. */
    done: hex.optional(),
    /** A fill behind black content (the key on the welcome screen). */
    highlight: hex.optional(),
    /** Up to three features named on the move-in welcome, e.g. furnished, maintenance. */
    features: z
        .array(z.object({
        label: z.string().trim().min(1).max(30).regex(NO_CONTROL, 'No control characters'),
        iconUrl: asset.optional(),
    }))
        .max(3)
        .optional(),
});
/** The stored JSON as a brand, or null when absent or not valid. */
export function parseTenantBrand(value) {
    if (value === null || value === undefined)
        return null;
    const r = tenantBrandSchema.safeParse(value);
    if (!r.success)
        return null;
    return Object.keys(r.data).length ? r.data : null;
}
// Readers never parse the profile themselves: the server's resolveBrand
// (packages/api, brand-resolve.ts) is the only one, and serves a
// ResolvedBrand ('./brand-public') to every surface.
// Colour helpers live in their own zod-free module, so a small app can import
// them alone ('@tendedloop/shared/brand-colors').
export { bandColors, contrastRatio, ensureContrast, mixColor } from './brand-colors';
//# sourceMappingURL=tenant-brand.js.map